import React, { useState, useEffect, useCallback } from 'react';
import './AdminPanel.css';

const AdminDashboard = () => {
  const [key, setKey] = useState(() => sessionStorage.getItem('vtd_admin_key') || '');
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [hours, setHours] = useState({ openHour: 10, closeHour: 22 });
  const [socialLinks, setSocialLinks] = useState({
    instagram: '', youtube: '', telegram: '', facebook: '', whatsapp: '',
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const tryAuth = useCallback(async (adminKey) => {
    setLoading(true);
    setMsg('');
    try {
      const res = await fetch('/api/admin/auth', {
        headers: { 'x-admin-key': adminKey },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setAuthed(true);
        setStats(data.stats || {});
        if (data.settings?.businessHours) setHours(data.settings.businessHours);
        sessionStorage.setItem('vtd_admin_key', adminKey);

        // Load social links separately (existing endpoint)
        try {
          const sRes = await fetch('/api/social-links');
          const sData = await sRes.json();
          if (sData.success && sData.links) {
            setSocialLinks(prev => ({ ...prev, ...sData.links }));
          }
        } catch {}
      } else {
        setAuthed(false);
        setMsg(data.error || 'Wrong admin key');
      }
    } catch {
      setMsg('Network error');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (key) tryAuth(key);
  }, [key, tryAuth]);

  const saveHours = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      const res = await fetch('/api/admin/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-key': key },
        body: JSON.stringify({ businessHours: hours }),
      });
      const data = await res.json();
      setMsg(data.success ? 'Hours saved ✓' : (data.error || 'Save failed'));
    } catch { setMsg('Network error'); }
  };

  const saveSocial = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      const res = await fetch('/api/save-social-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...socialLinks, adminPassword: key }),
      });
      const data = await res.json();
      setMsg(data.success ? 'Social links saved ✓' : (data.error || 'Save failed'));
    } catch { setMsg('Network error'); }
  };

  const resetOnboarding = () => {
    try {
      localStorage.removeItem('vtd_onboarded');
      localStorage.removeItem('vtd_pwa_onboarded');
      sessionStorage.removeItem('vtd_splash_played');
      setMsg('Onboarding reset — open / to see slides');
    } catch { setMsg('Could not reset'); }
  };

  if (!authed) {
    return (
      <div className="adm">
        <div className="adm__login">
          <h1>VTD Astrology — Admin</h1>
          <p className="adm__sub">Enter admin key</p>
          <input
            type="password"
            placeholder="Admin key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && tryAuth(key)}
          />
          <button onClick={() => tryAuth(key)} disabled={loading || !key}>
            {loading ? 'Checking…' : 'Unlock'}
          </button>
          {msg && <p className="adm__err">{msg}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="adm">
      <header className="adm__header">
        <h1>Admin Dashboard</h1>
        <p className="adm__sub">All site settings in one place</p>
      </header>

      {/* Business hours */}
      <section className="adm__card">
        <h2>Business Hours</h2>
        <p className="adm__sub">When the site shows the team as online. Outside these hours users see “Opens at …”.</p>
        <form className="adm__form" onSubmit={saveHours}>
          <div className="adm__row">
            <label>
              Open from
              <select
                value={hours.openHour}
                onChange={(e) => setHours({ ...hours, openHour: parseInt(e.target.value) })}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{formatHour(h)}</option>
                ))}
              </select>
            </label>
            <label>
              Close at
              <select
                value={hours.closeHour}
                onChange={(e) => setHours({ ...hours, closeHour: parseInt(e.target.value) })}
              >
                {Array.from({ length: 25 }, (_, h) => (
                  <option key={h} value={h}>{formatHour(h)}</option>
                ))}
              </select>
            </label>
          </div>
          <button type="submit">Save Hours</button>
        </form>
      </section>

      {/* Social Media Links */}
      <section className="adm__card">
        <h2>Social Media Links</h2>
        <form className="adm__form" onSubmit={saveSocial}>
          <div className="adm__row">
            <input placeholder="Instagram URL" value={socialLinks.instagram || ''}
              onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })} />
            <input placeholder="YouTube URL" value={socialLinks.youtube || ''}
              onChange={(e) => setSocialLinks({ ...socialLinks, youtube: e.target.value })} />
          </div>
          <div className="adm__row">
            <input placeholder="Telegram URL" value={socialLinks.telegram || ''}
              onChange={(e) => setSocialLinks({ ...socialLinks, telegram: e.target.value })} />
            <input placeholder="Facebook URL" value={socialLinks.facebook || ''}
              onChange={(e) => setSocialLinks({ ...socialLinks, facebook: e.target.value })} />
          </div>
          <div className="adm__row">
            <input placeholder="WhatsApp number (e.g. 919876543210)" value={socialLinks.whatsapp || ''}
              onChange={(e) => setSocialLinks({ ...socialLinks, whatsapp: e.target.value })} />
          </div>
          <button type="submit">Save Social Links</button>
        </form>
      </section>

      {/* Stats */}
      <section className="adm__card">
        <h2>Platform Overview</h2>
        <div className="adm__stats">
          <div className="adm__stat">
            <span className="adm__stat-num">{stats?.totalReviews || 0}</span>
            <span className="adm__stat-label">Total Reviews</span>
          </div>
          <div className="adm__stat">
            <span className="adm__stat-num">{stats?.communityPosts || 0}</span>
            <span className="adm__stat-label">Community Posts</span>
          </div>
        </div>
      </section>

      {/* Management tools */}
      <section className="adm__card">
        <h2>Management Tools</h2>
        <div className="adm__actions">
          <a href="/admin/reviews" className="adm__action">
            <div className="adm__action-icon">📝</div>
            <div><strong>Reviews</strong><p>Add, approve & delete reviews</p></div>
          </a>
          <a href="/admin/community" className="adm__action">
            <div className="adm__action-icon">🏛️</div>
            <div><strong>Community Posts</strong><p>Updates & announcements</p></div>
          </a>
          <a href="/admin/training" className="adm__action">
            <div className="adm__action-icon">🧠</div>
            <div><strong>AI Training</strong><p>Teach chat responses</p></div>
          </a>
          <a href="/master" target="_blank" rel="noopener noreferrer" className="adm__action">
            <div className="adm__action-icon">🛰️</div>
            <div><strong>Master AI Console</strong><p>System status & controls</p></div>
          </a>
          <a href="/chat/admin" target="_blank" rel="noopener noreferrer" className="adm__action">
            <div className="adm__action-icon">💬</div>
            <div><strong>Chat Admin</strong><p>Live conversations inbox</p></div>
          </a>
        </div>
      </section>

      {/* Quick links + utilities */}
      <section className="adm__card">
        <h2>Testing & Quick Links</h2>
        <div className="adm__links">
          <a href="/?onboard=1">🎬 Replay Onboarding Slides</a>
          <a href="/?intro=1">🌅 Replay Splash Animation</a>
          <a href="/appdownload" target="_blank" rel="noopener noreferrer">📱 App Download Page</a>
          <a href="/community" target="_blank" rel="noopener noreferrer">🌟 Community Page</a>
          <a href="/reviews" target="_blank" rel="noopener noreferrer">⭐ Reviews Page</a>
          <button onClick={resetOnboarding} className="adm__linkbtn">↺ Reset onboarding on this device</button>
        </div>
        {msg && <p className="adm__msg">{msg}</p>}
      </section>
    </div>
  );
};

function formatHour(h) {
  if (h === 0)  return '12 AM (midnight)';
  if (h === 12) return '12 PM (noon)';
  if (h === 24) return '12 AM (next day)';
  if (h < 12)   return `${h} AM`;
  return `${h - 12} PM`;
}

export default AdminDashboard;
